import React, { useState, useEffect } from "react";
import "./NoCallEntries.css";
import * as ApiService from "../services/ApiService";
import DoctorChip from "./DoctorChip";
import { useAuth0 } from "@auth0/auth0-react";

function NoCallEntries() {
  const { getAccessTokenSilently } = useAuth0();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [leaves, setLeaves] = useState([]);

  useEffect(() => {
    fetchLeaves();
  }, [currentDate]);

  // Add keyboard event listener
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (event.key === "ArrowLeft") {
        navigateMonth(-1);
      } else if (event.key === "ArrowRight") {
        navigateMonth(1);
      }
    };

    window.addEventListener("keydown", handleKeyPress);

    // Cleanup listener on component unmount
    return () => {
      window.removeEventListener("keydown", handleKeyPress);
    };
  }, []); // Empty dependency array since navigateMonth is stable

  const fetchLeaves = async () => {
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;
      const data = await ApiService.fetchLeavesByMonth(
        getAccessTokenSilently,
        year,
        month
      );

      // Group leaves by doctor
      const groupedLeaves = data.reduce((acc, leave) => {
        // Skip entries where Doctor is null or undefined - deleted doctors
        if (!leave.Doctor) {
          return acc;
        }

        const doctorId = leave.Doctor.staffId;
        if (!acc[doctorId]) {
          acc[doctorId] = {
            doctor: leave.Doctor,
            leaves: [],
          };
        }
        acc[doctorId].leaves.push(leave);
        return acc;
      }, {});

      // Convert to array and sort by doctor abbreviation
      setLeaves(
        Object.values(groupedLeaves).sort((a, b) =>
          a.doctor.abbreviation.localeCompare(b.doctor.abbreviation)
        )
      );
    } catch (error) {
      console.error("Error fetching leaves:", error);
    }
  };

  const navigateMonth = (direction) => {
    setCurrentDate((prevDate) => {
      const newDate = new Date(prevDate);
      newDate.setMonth(prevDate.getMonth() + direction);
      return newDate;
    });
  };

  const formatDateRange = (startDate, endDate) => {
    if (startDate === endDate) {
      return <strong>{startDate}</strong>;
    }
    return (
      <span>
        <strong>{startDate}</strong> to <strong>{endDate}</strong>
      </span>
    );
  };

  const formatReason = (reason) => {
    if (!reason) return null;

    // Check if the reason contains bold markers
    if (reason.includes("**")) {
      const parts = reason.split(/(\*\*[^*]+\*\*)/);
      return parts.map((part, index) => {
        if (part.startsWith("**") && part.endsWith("**")) {
          return <strong key={index}>{part.slice(2, -2)}</strong>;
        }
        return <span key={index}>{part}</span>;
      });
    }

    return reason;
  };

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  return (
    <div className="no-call-entries">
      <div className="page-header">
        <h2>No-Call Entries</h2>
      </div>
      <div className="month-navigation">
        <button onClick={() => navigateMonth(-1)} className="nav-button">
          &lt;
        </button>
        <h2 className="current-month">
          {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
        </h2>
        <button onClick={() => navigateMonth(1)} className="nav-button">
          &gt;
        </button>
      </div>

      <div className="no-call-table-container">
        {leaves.length === 0 ? (
          <div className="table-no-data">
            There are no entries for this month.
          </div>
        ) : (
          <table className="no-call-table">
            <thead>
              <tr>
                <th>Doctor</th>
                <th>Period</th>
                <th>Reason</th>
              </tr>
            </thead>
            <tbody>
              {leaves.map((doctorLeaves) =>
                doctorLeaves.leaves.map((leave, index) => (
                  <tr key={leave.leaveId}>
                    {index === 0 && (
                      <td
                        rowSpan={doctorLeaves.leaves.length}
                        className="doctor-cell"
                      >
                        <DoctorChip doctor={doctorLeaves.doctor} />
                      </td>
                    )}
                    <td className="period-cell">
                      {formatDateRange(leave.startDate, leave.endDate)}
                    </td>
                    <td className="reason-cell">
                      {formatReason(
                        leave.leaveType === "Other"
                          ? leave.customLeaveType
                          : leave.leaveType
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default NoCallEntries;
